import { useSelector, useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Notification as AppNotification,
  removeNotification,
  markAsRead,
} from "../../store/notifications.slice";
import { RootState } from "../../store";
import "./NotificationStack.scss";
import { CloseOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { getSession } from "../../utils/session";
import { hasValidAuthSession } from "../../utils/firebaseAuth";
import shopService from "../../services/allShops";
import {
  isCreatedOrderId,
  removeCreatedOrderId,
} from "../../utils/createdOrderIds";
import {
  requestNotificationPermission,
  showOrderNotification,
  canShowNotifications,
} from "../../utils/systemNotifications";
import { formatPrice } from "../../utils/formatPrice";
import { shouldSuppressNotification } from "../../utils/notificationFilters";

type ShopSummary = {
  firestore?: { id?: string };
  ondc?: { provider_details?: { id?: string } };
};

const NotificationStack = () => {
  const notifications = useSelector(
    (state: RootState) => state.notifications.notifications
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldAutoStack, setShouldAutoStack] = useState(false);
  const [shopList, setShopList] = useState<ShopSummary[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const shopListFetchedRef = useRef(false);
  const isLoadingShopsRef = useRef(false);
  const processedOrderIdsRef = useRef<Set<number>>(new Set());
  const hasAuthSession = hasValidAuthSession();
  const userId = getSession("userId");
  const isLoggedIn = useMemo(
    () => Boolean(hasAuthSession && userId),
    [hasAuthSession, userId]
  );
  const [notificationPermissionRequested, setNotificationPermissionRequested] =
    useState(false);
  const shownSystemNotificationsRef = useRef<Set<string>>(new Set());
  const autoStackTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check if we're on the order details page
  const isOnOrderDetailsPage = useMemo(() => {
    return /^\/orders\/[^/]+$/.test(location.pathname);
  }, [location.pathname]);

  const fetchShopList = useCallback(async () => {
    // Check if user is logged in
    const hasAuthSession = hasValidAuthSession();
    const userId = getSession("userId");

    if (!hasAuthSession || !userId) {
      return;
    }

    // Check if shop list is already in session storage
    const cachedShopList = sessionStorage.getItem("shopList");
    if (cachedShopList) {
      try {
        const parsedShopList = JSON.parse(cachedShopList) as ShopSummary[];
        if (Array.isArray(parsedShopList)) {
          setShopList(parsedShopList);
          shopListFetchedRef.current = true;
          return;
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error parsing cached shop list:", error);
      }
    }

    // Prevent duplicate API calls
    if (shopListFetchedRef.current || isLoadingShopsRef.current) {
      return;
    }

    shopListFetchedRef.current = true;
    isLoadingShopsRef.current = true;
    setIsLoadingShops(true);

    try {
      const response = await shopService.fetchAllShopsForLogin();
      const shops =
        (response?.data?.result?.data?.shops as ShopSummary[]) || [];

      // Store in state and session storage
      setShopList(shops);
      sessionStorage.setItem("shopList", JSON.stringify(shops));
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error fetching shop list:", error);
      shopListFetchedRef.current = false;
      isLoadingShopsRef.current = false;
    } finally {
      setIsLoadingShops(false);
      isLoadingShopsRef.current = false;
    }
  }, []);

  // Request notification permission on mount if user is logged in
  useEffect(() => {
    if (!isLoggedIn || notificationPermissionRequested) return;

    const requestPermission = async () => {
      try {
        await requestNotificationPermission();
        setNotificationPermissionRequested(true);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error requesting notification permission:", error);
      }
    };

    requestPermission();
  }, [isLoggedIn, notificationPermissionRequested]);

  // Fetch shop list on mount if user is logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchShopList();
  }, [fetchShopList, isLoggedIn]);

  // Fetch shop list again when notifications arrive and list is still empty
  useEffect(() => {
    if (
      !isLoggedIn ||
      notifications.length === 0 ||
      shopList.length > 0 ||
      isLoadingShops
    ) {
      return;
    }

    fetchShopList();
  }, [
    fetchShopList,
    isLoadingShops,
    isLoggedIn,
    notifications.length,
    shopList.length,
  ]);

  // Clear shop list from session storage when user logs out
  useEffect(() => {
    if (!hasAuthSession || !userId) {
      sessionStorage.removeItem("shopList");
      setShopList([]);
      shopListFetchedRef.current = false;
    }
  }, [hasAuthSession, userId]);

  const handleClose = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(markAsRead(id)); // Mark as read to update badge
    dispatch(removeNotification(id));
  };

  const handleNotificationClick = (notification: AppNotification) => {
    dispatch(markAsRead(notification.id)); // Mark as read when clicked
    
    if (notification.data?.url) {
      navigate(notification.data.url);
      dispatch(removeNotification(notification.id));
    } else if (
      notification.data?.externalOrderId &&
      notification.data?.type === "order"
    ) {
      // Navigate to order details page using externalOrderId in the path
      navigate(`/orders/${notification.data.externalOrderId}`);
      dispatch(removeNotification(notification.id));
    } else if (notification.data?.orderId) {
      // Fallback for old format
      navigate(`/orders/${notification.data.orderId}`);
      dispatch(removeNotification(notification.id));
    }
  };

  // Filter notifications to show only new incoming order notifications from known shops
  const [filteredNotifications, orderIdsToRemove] = useMemo(() => {
    if (!isLoggedIn) {
      return [[], []];
    }

    // Collect orderIds that should be filtered out (but don't remove them yet)
    const orderIdsToRemoveSet = new Set<number>();

    const filtered = notifications?.filter((notification) => {
      const notificationData = notification?.data || {};

      // TODO: Normalize notification data at the source to avoid checking multiple keys for subType.
      const subType =
        notificationData?.subType ||
        notificationData?.subtype ||
        notificationData?.["sub type"];

      const isOrderNotification =
        notificationData?.type === "order" && subType === "incoming";

      if (!isOrderNotification) {
        return false;
      }

      // Suppress offline order notifications
      if (shouldSuppressNotification(notificationData)) {
        return false;
      }

      // Check if orderId exists in the notification data
      const notificationOrderId = notificationData?.orderId;

      if (notificationOrderId !== undefined && notificationOrderId !== null) {
        // Convert to number - handle both string and number types
        const orderIdNumber =
          typeof notificationOrderId === "string"
            ? Number(notificationOrderId)
            : Number(notificationOrderId);

        // Only check if conversion was successful (not NaN)
        if (!isNaN(orderIdNumber)) {
          // Check if we've already processed this orderId or if it exists in sessionStorage
          const isInCreatedOrders = isCreatedOrderId(orderIdNumber);
          const isAlreadyProcessed =
            processedOrderIdsRef.current.has(orderIdNumber);

          if (isInCreatedOrders || isAlreadyProcessed) {
            // Mark this orderId as processed and for removal
            processedOrderIdsRef.current.add(orderIdNumber);
            orderIdsToRemoveSet.add(orderIdNumber);
            return false;
          }
        }
      }

      const shopId = notificationData?.shopId;

      // Allow notifications without shopId (backwards compatibility)
      if (!shopId) {
        return true;
      }

      // If shop list is not loaded yet, suppress until we know
      if (shopList.length === 0) {
        return false;
      }

      return shopList.some((shop: ShopSummary) => {
        const firestoreId = shop?.firestore?.id;
        const ondcProviderId = shop?.ondc?.provider_details?.id;
        return firestoreId === shopId || ondcProviderId === shopId;
      });
    });

    return [filtered || [], Array.from(orderIdsToRemoveSet)];
  }, [isLoggedIn, notifications, shopList]);

  // Remove collected orderIds from sessionStorage after filtering
  useEffect(() => {
    if (orderIdsToRemove.length > 0) {
      orderIdsToRemove.forEach((orderId) => {
        removeCreatedOrderId(orderId);
      });
    }
  }, [orderIdsToRemove]);

  // Show system tray notifications for new order notifications
  useEffect(() => {
    if (!isLoggedIn || !canShowNotifications()) {
      return;
    }

    filteredNotifications.forEach((notification) => {
      // Only show system notification for order notifications that haven't been shown yet
      const notificationData = notification?.data || {};
      const subType =
        notificationData?.subType ||
        notificationData?.subtype ||
        notificationData?.["sub type"];

      const isOrderNotification =
        notificationData?.type === "order" && subType === "incoming";

      if (!isOrderNotification) {
        return;
      }

      // Check if we've already shown a system notification for this notification
      if (shownSystemNotificationsRef.current.has(notification.id)) {
        return;
      }

      // Mark as shown
      shownSystemNotificationsRef.current.add(notification.id);

      // Show system tray notification
      try {
        // Build order data object with only defined fields
        const orderNotificationData: {
          orderId?: number;
          externalOrderId?: string;
          shopId?: string;
          type?: string;
          subType?: string;
          url?: string;
          customerName?: string;
          customerPhone?: string;
          orderTotal?: string;
        } = {
          orderId: notificationData?.orderId,
          externalOrderId: notificationData?.externalOrderId,
          shopId: notificationData?.shopId,
          type: notificationData?.type,
          subType: subType,
          url: notificationData?.url,
        };
        
        // Only include optional fields if they exist
        if (notificationData?.customerName) {
          orderNotificationData.customerName = notificationData.customerName;
        }
        if (notificationData?.customerPhone) {
          orderNotificationData.customerPhone = notificationData.customerPhone;
        }
        if (notificationData?.orderTotal) {
          // Format the order total for display
          const formattedTotal = formatPrice(notificationData.orderTotal);
          orderNotificationData.orderTotal = formattedTotal || String(notificationData.orderTotal);
        }
        
        const systemNotification = showOrderNotification(
          notification.title || "New Order",
          notification.body || "",
          orderNotificationData
        );

        // Handle notification click
        if (systemNotification) {
          systemNotification.onclick = () => {
            // Focus the window
            window.focus();

            // Navigate to order details
            if (notificationData?.externalOrderId) {
              navigate(`/orders/${notificationData.externalOrderId}`);
            } else if (notificationData?.orderId) {
              navigate(`/orders/${notificationData.orderId}`);
            } else if (notificationData?.url) {
              navigate(notificationData.url);
            }

            // Remove the in-app notification
            dispatch(removeNotification(notification.id));

            // Close the system notification
            systemNotification.close();
          };

          // Don't auto-close - let requireInteraction handle persistence
          // The notification will stay in the tray until user interacts with it
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error showing system notification:", error);
      }
    });
  }, [filteredNotifications, isLoggedIn, navigate, dispatch]);

  useEffect(() => {
    if (containerRef.current && filteredNotifications.length > 0) {
      containerRef.current.scrollTop = 0;
    }
  }, [filteredNotifications.length]);

  // Auto-stack notifications after showing the new one briefly
  useEffect(() => {
    // Clear any existing timer
    if (autoStackTimerRef.current) {
      clearTimeout(autoStackTimerRef.current);
    }

    // If there are multiple notifications, show the new one for 3 seconds then stack
    if (filteredNotifications.length > 1) {
      // Reset to unstacked state to show the new notification
      setShouldAutoStack(false);

      // Set timer to auto-stack after 3 seconds
      autoStackTimerRef.current = setTimeout(() => {
        setShouldAutoStack(true);
      }, 3000);
    } else if (filteredNotifications.length === 0) {
      // Reset states when all notifications are dismissed
      setShouldAutoStack(false);
      setIsHovered(false);
    } else {
      // Single notification, no need to stack
      setShouldAutoStack(false);
    }

    // Cleanup timer on unmount or when notifications change
    return () => {
      if (autoStackTimerRef.current) {
        clearTimeout(autoStackTimerRef.current);
      }
    };
  }, [filteredNotifications.length]);

  // Reset auto-stack when hovering
  const handleMouseEnter = () => {
    setIsHovered(true);
    // Clear the auto-stack timer when user hovers
    if (autoStackTimerRef.current) {
      clearTimeout(autoStackTimerRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Re-enable auto-stacking when mouse leaves
    if (filteredNotifications.length > 1) {
      setShouldAutoStack(true);
    }
  };

  // Don't show in-app notifications on order details page
  if (filteredNotifications.length === 0 || isOnOrderDetailsPage) {
    return null;
  }

  const shouldStack = filteredNotifications.length > 1 && shouldAutoStack && !isHovered;

  const getStackClass = (index: number) => {
    if (!shouldStack) return "";
    if (index <= 2) return `stack-item stack-item-${index}`;
    return "stack-item stack-item-hidden";
  };

  return (
    <div
      className={`notification-stack-container ${shouldStack ? "stacked" : ""} ${isHovered ? "expanded" : ""}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="notification-list">
        {filteredNotifications.map((notification, index) => {
          const notificationData = notification?.data || {};
          const subType =
            notificationData?.subType ||
            notificationData?.subtype ||
            notificationData?.["sub type"];
          const isIncomingOrder =
            notificationData?.type === "order" && subType === "incoming";
          
          // Check if any order details exist
          const hasOrderDetails = isIncomingOrder && (
            notificationData?.customerName ||
            notificationData?.customerPhone ||
            notificationData?.orderTotal
          );
          
          return (
            <div
              key={notification.id}
              className={`notification-item ${getStackClass(index)}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-content">
                <div className="notification-header">
                  <h4 className="notification-title">{notification.title}</h4>
                  <button
                    className="notification-close-btn"
                    onClick={(e) => handleClose(notification.id, e)}
                    aria-label="Close notification"
                  >
                    <CloseOutlined />
                  </button>
                </div>
                {(notification.body || hasOrderDetails) && (
                  <div className="notification-body">
                    {notification.body}
                    {hasOrderDetails && (
                      <>
                        {notificationData?.customerName && (
                          <>
                            {notification.body ? '\n' : ''}Name: {notificationData.customerName}
                          </>
                        )}
                        {notificationData?.customerPhone && (
                          <>
                            {'\nContact No: '}
                            {notificationData.customerPhone}
                          </>
                        )}
                        {notificationData?.orderTotal && (
                          <>
                            {'\nOrder Total: '}
                            {formatPrice(notificationData.orderTotal) || `₹${notificationData.orderTotal}`}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {notification.timestamp && (
                  <div className="notification-time">
                    {formatTimestamp(notification.timestamp)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {/* {remainingCount > 0 && (
          <div className="notification-stack-indicator">
            +{remainingCount} more
          </div>
        )} */}
      </div>
    </div>
  );
};

const formatTimestamp = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) {
    return "Just now";
  } else if (minutes < 60) {
    return `${minutes}m ago`;
  } else if (hours < 24) {
    return `${hours}h ago`;
  } else {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  }
};

export default NotificationStack;
